/**
 * Durable pending-sync queue model for patient writes (#1739).
 *
 * Pure module (no React/RN/storage imports) so ordering, retry, and
 * failure semantics are unit-testable in plain node. Persistence and
 * transport live in the sync manager.
 *
 * Invariants:
 * - Strictly FIFO with one write in flight: entry N+1 is never sent
 *   before N settles, so a delayed retry cannot overwrite a newer answer
 *   (the out-of-order half of the #1739 contract; the server's
 *   Idempotency-Key replay handles duplicate delivery).
 * - `id` doubles as the Idempotency-Key: generated once at durable acceptance
 *   and stable across every retry of that write.
 * - Permanent failures leave the active queue but are retained in
 *   `failed` so the answer can be surfaced and recovered, and so one
 *   poisoned write cannot starve every later section save.
 */

import {
  collectionIdentityKeysForScope,
  collectionRowIdentities,
} from "./mergePatientUpdate";

export type PendingPatientWrite = {
  /** Client-generated UUID; sent as the Idempotency-Key on every attempt. */
  id: string;
  /** The PATCH body (UpdatePatientInput minus transport-only fields). */
  body: Record<string, unknown>;
  /** Original logical request identity, retained even when server IDs rebase body. */
  fingerprint: string;
  /** ISO timestamp of when the answer was captured. */
  enqueuedAt: string;
  /** Monotonic per-patient acceptance order. */
  generation: number;
  attempts: number;
};

export type FailedPatientWrite = PendingPatientWrite & {
  failedAt: string;
  /** Client classification of the permanent failure. */
  reason: "validation" | "auth" | "identity" | "conflict";
  message: string;
  /** The answer remains retained; only its attention banner was dismissed. */
  warningDismissed?: boolean;
};

export type PatientWriteQueueState = {
  version: 1;
  pending: PendingPatientWrite[];
  failed: FailedPatientWrite[];
};

export const EMPTY_QUEUE: PatientWriteQueueState = {
  version: 1,
  pending: [],
  failed: [],
};

const STORAGE_PREFIX = "louise.patient-sync-queue.v1";

/** A queue belongs to exactly one tenant/patient pair on this device. */
export function patientQueueStorageKey(tenantId: string, patientId: string) {
  return `${STORAGE_PREFIX}.${encodeURIComponent(tenantId)}.${encodeURIComponent(patientId)}`;
}

export function enqueueWrite(
  state: PatientWriteQueueState,
  write: PendingPatientWrite,
): PatientWriteQueueState {
  return { ...state, pending: [...state.pending, write] };
}

function collectWriteScopes(
  value: unknown,
  prefix: string,
  scopes: Map<string, unknown>,
): void {
  if (Array.isArray(value)) {
    if (prefix) scopes.set(prefix, value);
    return;
  }
  if (!isRecord(value)) {
    if (prefix) scopes.set(prefix, value);
    return;
  }
  const entries = Object.entries(value).filter(
    ([key]) => key !== "id" && key !== "op",
  );
  if (entries.length === 0 && prefix) scopes.set(prefix, value);
  for (const [key, nested] of entries) {
    collectWriteScopes(nested, prefix ? `${prefix}.${key}` : key, scopes);
  }
}

/** Scope path → the value written at that scope (arrays kept whole). */
function writeScopeValues(body: Record<string, unknown>): Map<string, unknown> {
  const scopes = new Map<string, unknown>();
  collectWriteScopes(body, "", scopes);
  return scopes;
}

/** Answer fields of a row, normalized like reconcileCollection. */
function rowFieldKeys(row: Record<string, unknown>): string[] {
  return Object.keys(row).filter(
    (key) => key !== "id" && key !== "op" && row[key] !== undefined,
  );
}

function rowIdentityMatches(
  parkedRow: Record<string, unknown>,
  coveringRow: Record<string, unknown>,
  identityKeys: string[],
): boolean {
  const parkedId = typeof parkedRow.id === "string" ? parkedRow.id : undefined;
  const coveringId =
    typeof coveringRow.id === "string" ? coveringRow.id : undefined;
  // reconcileCollection gives an explicit operation id precedence over every
  // logical key. Two rows with different server ids therefore cannot cover
  // one another merely because they share a disease/substance/etc. value.
  if (coveringId !== undefined) return parkedId === coveringId;

  const parkedLogical = collectionRowIdentities(parkedRow, identityKeys).find(
    (identity) => identity.startsWith("key:"),
  );
  const coveringLogical = collectionRowIdentities(
    coveringRow,
    identityKeys,
  ).find((identity) => identity.startsWith("key:"));
  return (
    parkedLogical !== undefined &&
    coveringLogical !== undefined &&
    parkedLogical === coveringLogical
  );
}

function fieldValueCovered(
  scope: string,
  parkedValue: unknown,
  coveringValue: unknown,
): boolean {
  if (Array.isArray(parkedValue)) {
    return arrayValueCovered(scope, parkedValue, [coveringValue]);
  }
  if (!isRecord(parkedValue)) {
    // Presence was checked by rowCoveredBy. Explicit null/false/empty-string
    // are intentional replacements and therefore cover a scalar answer.
    return true;
  }
  if (!isRecord(coveringValue)) {
    // An explicit null or scalar replaces this whole nested object under the
    // deployed PATCH merge contract; undefined was filtered out earlier.
    return true;
  }
  for (const key of rowFieldKeys(parkedValue)) {
    if (
      !Object.prototype.hasOwnProperty.call(coveringValue, key) ||
      coveringValue[key] === undefined ||
      !fieldValueCovered(
        `${scope}.${key}`,
        parkedValue[key],
        coveringValue[key],
      )
    ) {
      return false;
    }
  }
  return true;
}

function isJsonScalar(value: unknown): boolean {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

/**
 * JSON-scalar arrays are scalar PATCH answers: a newer array at the same scope
 * replaces the entire older value, including with an empty array. Nested
 * arrays, arrays of records, and non-JSON values fail closed unless the scope
 * is a descriptor-backed operation collection.
 */
function arrayValueCovered(
  scope: string,
  parkedValue: unknown[],
  coveringValues: unknown[],
): boolean {
  const identityKeys = collectionIdentityKeysForScope(scope);
  if (identityKeys !== undefined) {
    return collectionRowsCovered(scope, parkedValue, coveringValues);
  }
  if (!parkedValue.every(isJsonScalar)) return false;
  return coveringValues.some(
    (value) => Array.isArray(value) && value.every(isJsonScalar),
  );
}

type NegativeParentCollectionRule = {
  path: readonly string[];
  negativeValue: boolean | string;
  additionalNegativeValues?: readonly (boolean | string)[];
  childScopes: readonly string[];
  /** Inapplicable scalar or scalar-array answers, independent of server data. */
  obsoleteWholeScopes?: readonly string[];
};

/**
 * Deployed parent answers whose negative value makes retained anonymous child
 * rows inapplicable. These rules are consumed only by the explicit exact
 * failed-write edit flow; ordinary queue compaction remains row-complete.
 */
const NEGATIVE_PARENT_COLLECTION_RULES: readonly NegativeParentCollectionRule[] =
  [
    {
      path: [
        "fertilityHistory",
        "infertilityHistory",
        "isDiagnosedWithInfertility",
      ],
      negativeValue: false,
      childScopes: [
        "fertilityHistory.infertilityHistory.causes",
        "fertilityHistory.infertilityHistory.diagnoses",
      ],
    },
    {
      path: ["obGynHistory", "hasBeenPregnant"],
      negativeValue: false,
      childScopes: ["obGynHistory.pregnancies"],
    },
    {
      path: ["obGynHistory", "hasContraceptionHistory"],
      negativeValue: false,
      childScopes: [
        "obGynHistory.contraceptions",
        "maleHistory.contraceptions",
      ],
    },
    {
      path: ["obGynHistory", "menstrualHealth", "hasPeriodSymptoms"],
      negativeValue: false,
      childScopes: ["obGynHistory.menstrualHealth.symptoms"],
    },
    {
      path: ["obGynHistory", "sexualHealth", "hadPapSmearProcedures"],
      negativeValue: false,
      childScopes: ["obGynHistory.sexualHealth.papSmearProcedures"],
    },
    {
      path: ["obGynHistory", "sexualHealth", "hadPapSmear"],
      negativeValue: false,
      childScopes: ["obGynHistory.sexualHealth.papSmearProcedures"],
      obsoleteWholeScopes: [
        "obGynHistory.sexualHealth.papSmearResultAbnormal",
        "obGynHistory.sexualHealth.hadPapSmearProcedures",
      ],
    },
    {
      path: ["obGynHistory", "sexualHealth", "papSmearResultAbnormal"],
      negativeValue: false,
      childScopes: ["obGynHistory.sexualHealth.papSmearProcedures"],
      obsoleteWholeScopes: ["obGynHistory.sexualHealth.hadPapSmearProcedures"],
    },
    {
      path: ["obGynHistory", "sexualHealth", "hadSTDs"],
      negativeValue: false,
      childScopes: ["obGynHistory.sexualHealth.stds"],
    },
    {
      path: ["obGynHistory", "sexualHealth", "hasVaginalInfectionHistory"],
      negativeValue: false,
      childScopes: ["obGynHistory.sexualHealth.vaginalInfectionHistories"],
    },
    {
      path: ["medicalHistory", "hasAllergies"],
      negativeValue: false,
      childScopes: ["medicalHistory.allergies"],
    },
    {
      path: ["medicalHistory", "currentlyTakingMedications"],
      negativeValue: false,
      childScopes: ["medicalHistory.medications"],
    },
    {
      path: ["medicalHistory", "hasSurgeries"],
      negativeValue: false,
      childScopes: ["medicalHistory.surgeries"],
    },
    {
      path: ["medicalHistory", "hasCancerHistory"],
      negativeValue: false,
      childScopes: ["medicalHistory.cancers"],
    },
    {
      path: ["medicalHistory", "hasBreastConditions"],
      negativeValue: false,
      childScopes: ["medicalHistory.breastConditions"],
    },
    {
      path: ["medicalHistory", "hasBreastTestings"],
      negativeValue: false,
      childScopes: ["testResults"],
    },
    {
      path: ["lifestyle", "hasAddictions"],
      negativeValue: false,
      childScopes: ["lifestyle.addictions"],
    },
    {
      path: ["lifestyle", "hasExposures"],
      negativeValue: false,
      childScopes: ["lifestyle.exposures"],
    },
    {
      path: ["lifestyle", "hasDiets"],
      negativeValue: false,
      childScopes: ["lifestyle.diets"],
    },
    {
      path: ["lifestyle", "hasDietSupplements"],
      negativeValue: false,
      childScopes: ["lifestyle.dietSupplements"],
    },
    {
      path: ["familyHistory", "hasRelativeFacingFertilityIssues"],
      negativeValue: "no",
      additionalNegativeValues: ["unknown"],
      childScopes: ["familyHistory.familyMemberWithFertilityIssues"],
    },
    {
      path: ["familyHistory", "adoptionStatus"],
      negativeValue: "adopted_or_dont_know_biological_parents",
      additionalNegativeValues: ["from_gamete_donation"],
      childScopes: ["familyHistory.familyMemberWithFertilityIssues"],
      obsoleteWholeScopes: [
        "familyHistory.hasRelativeFacingFertilityIssues",
        "familyHistory.hasMotherTakenDESDuringPregnancy",
        "familyHistory.diseasesMothersSide",
        "familyHistory.diseasesFathersSide",
      ],
    },
  ];

const DISEASE_CATEGORY_BY_NEGATIVE_FLAG: Readonly<Record<string, string>> = {
  hasEndocrineDiseases: "endocrine",
  hasInfectiousDiseases: "infectious",
  hasCardiovascularDiseases: "cardiovascular",
  hasRespiratoryDiseases: "respiratory",
  hasGastrointestinalDiseases: "gastrointestinal",
  hasHepaticBiliaryDiseases: "hepatic_biliary",
  hasRenalUrinaryDiseases: "renal_urinary",
  hasMusculoskeletalDiseases: "musculoskeletal",
  hasHaematologicalDiseases: "hematological",
  hasAutoimmuneDiseases: "autoimmune",
  hasGeneticDiseases: "genetic",
  hasEatingDisorders: "eating_disorders",
  hasNeurologicalMentalHealthDiseases: "neurological_mental_health",
};

function valueAtPath(
  root: Record<string, unknown>,
  path: readonly string[],
): unknown {
  let value: unknown = root;
  for (const key of path) {
    if (!isRecord(value)) return undefined;
    value = value[key];
  }
  return value;
}

type RecoveryCoverage = {
  authoritativeCollectionScopes: ReadonlySet<string>;
  obsoleteScopes: ReadonlySet<string>;
  obsoleteWholeScopes: ReadonlySet<string>;
  obsoleteDiseaseCategories: ReadonlySet<string>;
};

function recoveryCoverageForReplacement(
  replacement: Record<string, unknown>,
): RecoveryCoverage {
  const authoritativeCollectionScopes = new Set<string>();
  for (const [scope, value] of writeScopeValues(replacement)) {
    if (
      Array.isArray(value) &&
      collectionIdentityKeysForScope(scope) !== undefined
    ) {
      authoritativeCollectionScopes.add(scope);
    }
  }

  const obsoleteScopes = new Set<string>();
  const obsoleteWholeScopes = new Set<string>();
  for (const rule of NEGATIVE_PARENT_COLLECTION_RULES) {
    const parentValue = valueAtPath(replacement, rule.path);
    if (
      parentValue === rule.negativeValue ||
      ((typeof parentValue === "boolean" || typeof parentValue === "string") &&
        rule.additionalNegativeValues?.includes(parentValue) === true)
    ) {
      for (const childScope of rule.childScopes) {
        obsoleteScopes.add(childScope);
      }
      for (const wholeScope of rule.obsoleteWholeScopes ?? []) {
        obsoleteWholeScopes.add(wholeScope);
      }
    }
  }

  const obsoleteDiseaseCategories = new Set<string>();
  const medicalHistory = isRecord(replacement.medicalHistory)
    ? replacement.medicalHistory
    : {};
  for (const [flag, category] of Object.entries(
    DISEASE_CATEGORY_BY_NEGATIVE_FLAG,
  )) {
    if (medicalHistory[flag] === false) {
      obsoleteDiseaseCategories.add(category);
    }
  }

  return {
    authoritativeCollectionScopes,
    obsoleteScopes,
    obsoleteWholeScopes,
    obsoleteDiseaseCategories,
  };
}

function setValueAtScope(
  root: Record<string, unknown>,
  scope: string,
  value: unknown,
): void {
  const path = scope.split(".");
  let current = root;
  for (const key of path.slice(0, -1)) {
    const nested = current[key];
    const next = isRecord(nested) ? nested : {};
    current[key] = next;
    current = next;
  }
  current[path.at(-1)!] = value;
}

/**
 * Build an optimistic-only delete patch for anonymous rows contributed by the
 * parked failed body. Those rows never reached the server, but are already in
 * the local cache; applying these deletes before the accepted correction
 * prevents a clear or identity-changing edit from displaying both answers.
 *
 * Server-identified rows are deliberately excluded: hiding a durable server
 * row requires an explicit delete in the real replacement body.
 */
export function localCleanupForFailedReplacement(
  failedBody: Record<string, unknown>,
  replacement: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const recoveryCoverage = recoveryCoverageForReplacement(replacement);
  const cleanup: Record<string, unknown> = {};
  for (const [scope, value] of writeScopeValues(failedBody)) {
    if (recoveryCoverage.obsoleteWholeScopes.has(scope)) {
      setValueAtScope(cleanup, scope, Array.isArray(value) ? [] : null);
      continue;
    }
    if (
      !Array.isArray(value) ||
      collectionIdentityKeysForScope(scope) === undefined
    ) {
      continue;
    }
    const wholeCollectionIsReplaced =
      recoveryCoverage.authoritativeCollectionScopes.has(scope) ||
      recoveryCoverage.obsoleteScopes.has(scope);
    const anonymousRows = value
      .filter(
        (row): row is Record<string, unknown> =>
          isRecord(row) &&
          typeof row.id !== "string" &&
          (wholeCollectionIsReplaced ||
            (scope === "medicalHistory.diseases" &&
              typeof row.category === "string" &&
              recoveryCoverage.obsoleteDiseaseCategories.has(row.category))),
      )
      .map((row) => ({ ...row, op: "delete" }));
    if (anonymousRows.length > 0) {
      setValueAtScope(cleanup, scope, anonymousRows);
    }
  }
  return Object.keys(cleanup).length > 0 ? cleanup : undefined;
}

/**
 * One covering row fully addresses one parked row only when it is an
 * op:"delete" for that row, or it writes EVERY field present in the parked
 * row (recursive field superset) — a partial-field patch (a quick-exposure
 * stop-patch writes only {substance, frequency}) must not count as a
 * replacement for a detailed parked row's sibling fields. A nested
 * collection inside the parked row must itself be row-covered by the
 * covering row's value at the same key (fail closed when it is not
 * addressed or is not a known collection).
 */
function rowCoveredBy(
  scope: string,
  parkedRow: Record<string, unknown>,
  coveringRow: Record<string, unknown>,
): boolean {
  if (coveringRow.op === "delete") return true;
  for (const key of rowFieldKeys(parkedRow)) {
    if (
      !Object.prototype.hasOwnProperty.call(coveringRow, key) ||
      coveringRow[key] === undefined ||
      !fieldValueCovered(`${scope}.*.${key}`, parkedRow[key], coveringRow[key])
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Row-aware coverage of one parked collection. Collection writes are partial
 * row patches (a quick single-row toggle addresses ONE row, not the list),
 * so presence of the scope alone proves nothing: the parked rows are covered
 * only when the covering writes address EVERY parked row — matched by server
 * id or by the logical identity key that reconcileCollection uses, consumed
 * one covering row per parked row (duplicate-identity parked rows need one
 * covering row EACH), and each match must replace every field of its parked
 * row (see rowCoveredBy). Collections whose identity is positional (no
 * identity keys, e.g. pregnancies) are covered only by a single covering
 * array addressing at least every parked position, position by position.
 * Unknown collections and unidentifiable rows are never covered — when in
 * doubt, park.
 */
function collectionRowsCovered(
  scope: string,
  parkedRows: unknown[],
  coveringValues: unknown[],
): boolean {
  const identityKeys = collectionIdentityKeysForScope(scope);
  if (identityKeys === undefined) return false;
  const parkedRecords = parkedRows.filter(isRecord);
  if (parkedRecords.length !== parkedRows.length) return false;

  if (
    identityKeys.length === 0 &&
    !parkedRecords.every((row) => typeof row.id === "string")
  ) {
    return coveringValues.some((rows) => {
      if (!Array.isArray(rows) || rows.length < parkedRecords.length) {
        return false;
      }
      return parkedRecords.every((row, index) => {
        const candidate: unknown = rows[index];
        return isRecord(candidate) && rowCoveredBy(scope, row, candidate);
      });
    });
  }

  const coveringRows: Record<string, unknown>[] = [];
  for (const rows of coveringValues) {
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      if (isRecord(row)) coveringRows.push(row);
    }
  }
  const consumed = new Set<number>();
  return parkedRecords.every((row) => {
    if (collectionRowIdentities(row, identityKeys).length === 0) return false;
    const match = coveringRows.findIndex((candidate, index) => {
      if (consumed.has(index)) return false;
      if (!rowIdentityMatches(row, candidate, identityKeys)) return false;
      return rowCoveredBy(scope, row, candidate);
    });
    if (match < 0) return false;
    consumed.add(match);
    return true;
  });
}

/**
 * True when the given bodies together cover EVERY answer scope of the target
 * write's body. Shared full-coverage predicate for both drop-instead-of-park
 * (permanent failure with newer pending replacements) and park removal at
 * replacement time — partial coverage must never discard a retained answer.
 * Scalar scopes are covered by presence; collection scopes require row-aware
 * coverage (see collectionRowsCovered).
 */
function bodiesCoverAllScopes(
  target: PendingPatientWrite | FailedPatientWrite,
  coveringBodies: Record<string, unknown>[],
  recoveryCoverage?: RecoveryCoverage,
): boolean {
  const targetScopes = writeScopeValues(target.body);
  if (targetScopes.size === 0) return false;
  const coveringScopes = coveringBodies.map(writeScopeValues);
  for (const [scope, originalValue] of targetScopes) {
    if (recoveryCoverage?.obsoleteWholeScopes.has(scope)) continue;
    const value =
      Array.isArray(originalValue) && recoveryCoverage !== undefined
        ? originalValue.filter(
            (row) =>
              !isRecord(row) ||
              typeof row.id === "string" ||
              !(
                recoveryCoverage.obsoleteScopes.has(scope) ||
                recoveryCoverage.authoritativeCollectionScopes.has(scope) ||
                (scope === "medicalHistory.diseases" &&
                  typeof row.category === "string" &&
                  recoveryCoverage.obsoleteDiseaseCategories.has(row.category))
              ),
          )
        : originalValue;
    if (Array.isArray(value) && value.length === 0) continue;
    const coveringAtScope = coveringScopes.filter((scopes) =>
      scopes.has(scope),
    );
    if (coveringAtScope.length === 0) return false;
    if (!Array.isArray(value)) continue;
    if (
      !arrayValueCovered(
        scope,
        value,
        coveringAtScope.map((scopes) => scopes.get(scope)),
      )
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Whether one product recovery edit, plus writes captured after the parked
 * answer, replaces every still-relevant scope of that exact failure.
 * Recovery-only coverage is derived from the submitted body: explicit known
 * collection answers are authoritative for anonymous failed rows, negative
 * parents obsolete their anonymous child rows, and negative disease-category
 * flags obsolete anonymous rows only in that category. Server-identified rows
 * still require real operation coverage. Ordinary queue compaction never uses
 * this broader product-edit policy.
 */
export function replacementSupersedesFailed(
  state: PatientWriteQueueState,
  failedId: string,
  replacement: Record<string, unknown>,
): boolean {
  const failed = state.failed.find((write) => write.id === failedId);
  if (!failed) return false;
  return bodiesCoverAllScopes(
    failed,
    [
      replacement,
      ...state.pending
        .filter((candidate) => candidate.generation > failed.generation)
        .map((candidate) => candidate.body),
    ],
    recoveryCoverageForReplacement(replacement),
  );
}

/**
 * A newly edited answer atomically supersedes a parked failure once the
 * replacement is durable — but only when the replacement (together with
 * strictly newer still-pending writes) covers EVERY answer scope of the
 * parked body. A partial overlap keeps the failure parked: its non-overlapped
 * answers have no newer replacement, and removing the park would silently
 * make them unrecoverable. Unrelated failures remain visible and recoverable.
 */
export function enqueueWriteReplacingFailed(
  state: PatientWriteQueueState,
  write: PendingPatientWrite,
): PatientWriteQueueState {
  return {
    ...state,
    pending: [...state.pending, write],
    failed: state.failed.filter(
      (failed) =>
        !bodiesCoverAllScopes(failed, [
          write.body,
          ...state.pending
            .filter((candidate) => candidate.generation > failed.generation)
            .map((candidate) => candidate.body),
        ]),
    ),
  };
}

/**
 * True only when strictly newer pending writes cover EVERY answer scope of
 * the given write. Only then may a permanently failed head be dropped instead
 * of parked: each of its answers still has a newer durable replacement in the
 * queue. A partial overlap must park the head — dropping it would silently
 * discard the non-overlapped answers with no recovery banner.
 */
export function newerWritesSupersede(
  state: PatientWriteQueueState,
  write: PendingPatientWrite,
): boolean {
  return bodiesCoverAllScopes(
    write,
    state.pending
      .filter(
        (candidate) =>
          candidate.id !== write.id && candidate.generation > write.generation,
      )
      .map((candidate) => candidate.body),
  );
}

export function headWrite(
  state: PatientWriteQueueState,
): PendingPatientWrite | undefined {
  return state.pending[0];
}

/** Head write delivered (or replayed) successfully. */
export function resolveHead(
  state: PatientWriteQueueState,
  id: string,
): PatientWriteQueueState {
  if (state.pending[0]?.id !== id) return state;
  return { ...state, pending: state.pending.slice(1) };
}

/** Head write hit a transient failure — keep it first, count the attempt. */
export function recordAttempt(
  state: PatientWriteQueueState,
  id: string,
): PatientWriteQueueState {
  if (state.pending[0]?.id !== id) return state;
  const [head, ...rest] = state.pending;
  return {
    ...state,
    pending: [{ ...head!, attempts: head!.attempts + 1 }, ...rest],
  };
}

/** Head write failed permanently — retain it for recovery, unblock the rest. */
export function failHead(
  state: PatientWriteQueueState,
  id: string,
  reason: FailedPatientWrite["reason"],
  message: string,
  failedAt: string,
): PatientWriteQueueState {
  const head = state.pending[0];
  if (head?.id !== id) return state;
  const failedWrite = { ...head, failedAt, reason, message };
  const nextNewer = state.failed.findIndex(
    (write) => write.generation > failedWrite.generation,
  );
  const insertAt = nextNewer < 0 ? state.failed.length : nextNewer;
  return {
    version: 1,
    pending: state.pending.slice(1),
    failed: [
      ...state.failed.slice(0, insertAt),
      failedWrite,
      ...state.failed.slice(insertAt),
    ],
  };
}

export function dismissFailed(
  state: PatientWriteQueueState,
  id: string,
): PatientWriteQueueState {
  return {
    ...state,
    failed: state.failed.map((write) =>
      write.id === id ? { ...write, warningDismissed: true } : write,
    ),
  };
}

/** Remove a parked original only after a corrected replacement is durable. */
export function resolveFailed(
  state: PatientWriteQueueState,
  id: string,
): PatientWriteQueueState {
  if (!state.failed.some((write) => write.id === id)) return state;
  return { ...state, failed: state.failed.filter((write) => write.id !== id) };
}

/**
 * Put a parked write back into the FIFO with its original idempotency key,
 * inserted by ascending generation among the pending writes. The parked
 * write did NOT necessarily precede everything pending — another parked
 * failure retried earlier may already sit in the queue — so unconditional
 * prepending could deliver a newer generation before an older one and leave
 * the older value terminal on a shared answer scope. Generation order
 * restores capture order, so newer answers remain the final value.
 */
export function retryFailed(
  state: PatientWriteQueueState,
  id: string,
): PatientWriteQueueState {
  const failed = state.failed.find((write) => write.id === id);
  if (!failed) return state;
  const pending: PendingPatientWrite = {
    id: failed.id,
    body: failed.body,
    fingerprint: failed.fingerprint,
    enqueuedAt: failed.enqueuedAt,
    generation: failed.generation,
    attempts: 0,
  };
  const nextNewer = state.pending.findIndex(
    (candidate) => candidate.generation > pending.generation,
  );
  const insertAt = nextNewer < 0 ? state.pending.length : nextNewer;
  return {
    ...state,
    pending: [
      ...state.pending.slice(0, insertAt),
      pending,
      ...state.pending.slice(insertAt),
    ],
    failed: state.failed.filter((write) => write.id !== id),
  };
}

/** Exponential backoff with a ceiling: 1s, 2s, 4s, … capped at 60s. */
export function backoffMs(attempts: number): number {
  return Math.min(1000 * 2 ** Math.max(0, attempts - 1), 60_000);
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const FINGERPRINT_RE = /^[0-9a-f]{32}$/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPendingWrite(value: unknown): value is PendingPatientWrite {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    UUID_RE.test(value.id) &&
    isRecord(value.body) &&
    typeof value.fingerprint === "string" &&
    FINGERPRINT_RE.test(value.fingerprint) &&
    typeof value.enqueuedAt === "string" &&
    !Number.isNaN(Date.parse(value.enqueuedAt)) &&
    typeof value.generation === "number" &&
    Number.isSafeInteger(value.generation) &&
    value.generation >= 1 &&
    typeof value.attempts === "number" &&
    Number.isSafeInteger(value.attempts) &&
    value.attempts >= 0
  );
}

function isFailedWrite(value: unknown): value is FailedPatientWrite {
  return (
    isPendingWrite(value) &&
    typeof (value as { failedAt?: unknown }).failedAt === "string" &&
    typeof (value as { message?: unknown }).message === "string" &&
    ((value as { warningDismissed?: unknown }).warningDismissed === undefined ||
      typeof (value as { warningDismissed?: unknown }).warningDismissed ===
        "boolean") &&
    ["validation", "auth", "identity", "conflict"].includes(
      String((value as { reason?: unknown }).reason),
    )
  );
}

/**
 * Parse persisted queue state defensively: a corrupted or
 * foreign-version payload yields an empty queue rather than a crash —
 * losing a queue beats wedging every future save.
 */
export function parseQueueState(raw: string | null): PatientWriteQueueState {
  if (!raw) return EMPTY_QUEUE;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return EMPTY_QUEUE;
    if (
      parsed.version !== 1 ||
      !Array.isArray(parsed.pending) ||
      !Array.isArray(parsed.failed) ||
      !parsed.pending.every(isPendingWrite) ||
      !parsed.failed.every(isFailedWrite)
    ) {
      return EMPTY_QUEUE;
    }
    const ids = [...parsed.pending, ...parsed.failed].map((write) => write.id);
    if (new Set(ids).size !== ids.length) return EMPTY_QUEUE;
    return {
      version: 1,
      pending: [...parsed.pending].sort(
        (first, second) => first.generation - second.generation,
      ),
      failed: [...parsed.failed].sort(
        (first, second) => first.generation - second.generation,
      ),
    };
  } catch {
    return EMPTY_QUEUE;
  }
}

export function serializeQueueState(state: PatientWriteQueueState): string {
  return JSON.stringify(state);
}

export type MedicalHistoryWriteSection =
  | "basic-info"
  | "infertility"
  | "fertility-testing"
  | "fertility-treatments"
  | "menstrual-health"
  | "sexual-health"
  | "contraception"
  | "pregnancies"
  | "medications-vaccines"
  | "allergies"
  | "diseases"
  | "surgeries"
  | "exposure"
  | "substances"
  | "sleep-activity"
  | "diet-supplements"
  | "family-history";

export type PatientWriteSectionCounts = Partial<
  Record<MedicalHistoryWriteSection, number>
>;

const DISEASE_SECTION_FLAGS = new Set([
  ...Object.keys(DISEASE_CATEGORY_BY_NEGATIVE_FLAG),
  "hasBreastConditions",
  "hasBreastTestings",
  "hasCancerHistory",
  "hasEndometriosis",
  "suspectPCOS",
  "hadVaricoceles",
]);

/** Non-sensitive routing metadata for the failed-answer recovery surface. */
export function patientWriteSection(
  body: Record<string, unknown>,
): MedicalHistoryWriteSection | undefined {
  if (
    "dateOfBirth" in body ||
    "bodyData" in body ||
    "basicInfoCompleted" in body
  ) {
    return "basic-info";
  }
  const medical = isRecord(body.medicalHistory) ? body.medicalHistory : {};
  if ("allergies" in medical || "hasAllergies" in medical) return "allergies";
  if (
    "medications" in medical ||
    "vaccinations" in medical ||
    "currentlyTakingMedications" in medical
  ) {
    return "medications-vaccines";
  }
  if ("surgeries" in medical || "hasSurgeries" in medical) return "surgeries";
  if (
    "diseases" in medical ||
    "breastConditions" in medical ||
    "cancers" in medical ||
    Object.keys(medical).some((key) => DISEASE_SECTION_FLAGS.has(key))
  ) {
    return "diseases";
  }

  const obGyn = isRecord(body.obGynHistory) ? body.obGynHistory : {};
  const male = isRecord(body.maleHistory) ? body.maleHistory : {};
  if ("pregnancies" in obGyn || "hasBeenPregnant" in obGyn) {
    return "pregnancies";
  }
  if (
    "hasContraceptionHistory" in obGyn ||
    "contraceptions" in obGyn ||
    "contraceptions" in male
  ) {
    return "contraception";
  }
  if ("menstrualHealth" in obGyn) return "menstrual-health";
  if ("sexualHealth" in obGyn || "sexualHealth" in male) return "sexual-health";

  const fertility = isRecord(body.fertilityHistory)
    ? body.fertilityHistory
    : {};
  if ("infertilityHistory" in fertility) return "infertility";
  if (
    "hasPriorFertilityTreatments" in fertility ||
    "fertilityTreatments" in fertility
  ) {
    return "fertility-treatments";
  }
  if ("testResults" in body) return "fertility-testing";

  const lifestyle = isRecord(body.lifestyle) ? body.lifestyle : {};
  if ("exposures" in lifestyle || "hasExposures" in lifestyle) {
    return "exposure";
  }
  if ("addictions" in lifestyle || "hasAddictions" in lifestyle) {
    return "substances";
  }
  if (
    "hasDiets" in lifestyle ||
    "hasDietSupplements" in lifestyle ||
    "diets" in lifestyle ||
    "dietSupplements" in lifestyle ||
    "supplements" in lifestyle
  ) {
    return "diet-supplements";
  }
  if (
    Object.keys(lifestyle).some((key) =>
      /sleep|activity|exercise|sedentary/i.test(key),
    )
  ) {
    return "sleep-activity";
  }
  if ("familyHistory" in body) return "family-history";
  return undefined;
}

/**
 * Count retained writes by non-sensitive Medical History section metadata.
 * Unclassified patient writes, such as Plan Builder fertility intent, are
 * intentionally omitted so section-specific consumers do not over-block.
 */
export function countPatientWriteSections(
  writes: readonly { body: Record<string, unknown> }[],
): PatientWriteSectionCounts {
  const counts: PatientWriteSectionCounts = {};
  for (const write of writes) {
    const section = patientWriteSection(write.body);
    if (section) counts[section] = (counts[section] ?? 0) + 1;
  }
  return counts;
}
